import { supabase } from '@/lib/supabase';
import { ROOM_JOIN, stripRoomJoin } from '@/lib/data/room-scoped';
import type { RoomFeedbackRow } from '@/types/database';

/**
 * Client pushback on a room (migration 0017). Written only by the client, via
 * the submit_room_feedback RPC; the designer reads and resolves it here under
 * normal RLS.
 */
export async function listRoomFeedback(roomId: string): Promise<RoomFeedbackRow[]> {
  const { data, error } = await supabase
    .from('room_feedback')
    .select('*')
    .eq('room_id', roomId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data ?? [];
}

/**
 * Every open item across a project, for the room-card badges.
 *
 * RLS already scopes this to the signed-in designer's rooms, but the `in`
 * filter keeps it to one project so the dashboard doesn't pull every room the
 * designer owns.
 */
/**
 * Open feedback across a project, in one request.
 *
 * Took a room-id LIST until 2026-09-22, which meant it could not start until
 * the room query came back — the last round trip on a page that already had
 * five. Scoped through the room instead; see @/lib/data/room-scoped.
 */
export async function listOpenFeedbackForProject(projectId: string): Promise<RoomFeedbackRow[]> {
  const { data, error } = await supabase
    .from('room_feedback')
    .select(`*, ${ROOM_JOIN}`)
    .eq('rooms.project_id', projectId)
    .is('resolved_at', null)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return stripRoomJoin<RoomFeedbackRow>(data);
}

export async function resolveRoomFeedback(id: string): Promise<void> {
  const { error } = await supabase
    .from('room_feedback')
    .update({ resolved_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}
